import { matchPath, useLocation, Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// project-imports
import Dot from '@/components/base-component/extended/Dot';
import IconButton from '@/components/base-component/extended/IconButton';

import useConfig from '@/hooks/useConfig';
import { MenuOrientation, ThemeMode, NavActionType } from '@/config';
import { handlerDrawerOpen, useGetMenuMaster } from '@/api/menu';

// types
import { LinkTarget, NavItemType } from '@/types/bs-types/menu';

interface Props {
  item: NavItemType;
  level: number;
  isParents?: boolean;
}

// ==============================|| NAVIGATION - ITEM ||============================== //

export default function NavItem({ item, level, isParents = false }: Props) {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { mode, menuOrientation } = useConfig();

  const activeColor = '#10661A';

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const { pathname } = useLocation();

  const isSelected = !!matchPath(
    {
      path: item?.link ? item.link : item.url!,
      end: false
    },
    pathname
  );

  const textColor = mode === ThemeMode.DARK ? 'neutral.400' : 'neutral.main';
  const iconColor = isSelected ? activeColor : textColor;

  const Icon = item.icon as any;
  const iconSize = drawerOpen ? 20 : 22;

  const itemIcon = item.icon ? (
    <Icon
      variant="Bulk"
      size={iconSize}
      color={iconColor}
      style={{
        color: iconColor,
        ...(menuOrientation === MenuOrientation.HORIZONTAL && isParents && { fontSize: 20, stroke: '1.5' })
      }}
    />
  ) : (
    false
  );

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <Box sx={{ position: 'relative' }}>
          <ListItemButton
            component={Link}
            to={item.url!}
            target={itemTarget}
            disabled={item.disabled}
            selected={isSelected}
            sx={{
              zIndex: 1201,
              pl: drawerOpen ? `${level * 20}px` : 1.5,
              py: !drawerOpen && level === 1 ? 1.25 : 1,

              ...(drawerOpen && {
                '&:hover': {
                  bgcolor: 'transparent'
                },
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                  color: activeColor,
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: activeColor
                  }
                }
              }),

              ...(drawerOpen &&
                level === 1 && {
                  mx: 1.25,
                  my: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: mode === ThemeMode.DARK ? 'divider' : 'neutral.200'
                  },
                  '&.Mui-selected': {
                    color: activeColor,
                    '&:hover': {
                      color: activeColor
                    }
                  }
                }),

              ...(!drawerOpen && {
                px: 2.75,
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: 'transparent'
                },
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                  color: activeColor,
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: activeColor
                  }
                }
              })
            }}
            {...(downLG && { onClick: () => handlerDrawerOpen(false) })}
          >
            {itemIcon && (
              <ListItemIcon
                sx={{
                  minWidth: 38,
                  color: iconColor,

                  '& svg': {
                    color: iconColor
                  },

                  '& path': {
                    stroke: iconColor
                  },

                  ...(!drawerOpen &&
                    level === 1 && {
                      borderRadius: 1,
                      width: 46,
                      height: 46,
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        bgcolor: mode === ThemeMode.DARK ? 'neutral.light' : 'neutral.200'
                      }
                    }),

                  ...(!drawerOpen &&
                    isSelected && {
                      bgcolor: mode === ThemeMode.DARK ? 'neutral.100' : 'rgba(16, 102, 26, 0.12)',
                      '&:hover': {
                        bgcolor: mode === ThemeMode.DARK ? 'neutral.200' : 'rgba(16, 102, 26, 0.12)'
                      }
                    })
                }}
              >
                {itemIcon}
              </ListItemIcon>
            )}

            {!itemIcon && drawerOpen && (
              <ListItemIcon
                sx={{
                  minWidth: 30
                }}
              >
                <Dot size={isSelected ? 6 : 5} color={isSelected ? 'success' : 'neutral'} />
              </ListItemIcon>
            )}

            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      color: isSelected ? activeColor : textColor,
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {item.title}
                  </Typography>
                }
              />
            )}

            {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
              <Chip
                color={item.chip.color}
                variant={item.chip.variant}
                size={item.chip.size}
                label={item.chip.label}
                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              />
            )}
          </ListItemButton>

          {(drawerOpen || (!drawerOpen && level !== 1)) &&
            item?.actions &&
            item?.actions.map((action, index) => {
              const ActionIcon = action?.icon as any;
              const callAction = action?.function;

              return (
                <IconButton
                  key={index}
                  {...(action.type === NavActionType.FUNCTION && {
                    onClick: (event) => {
                      event.stopPropagation();
                      callAction();
                    }
                  })}
                  {...(action.type === NavActionType.LINK && {
                    component: Link,
                    to: action.url,
                    target: action.target ? '_blank' : '_self'
                  })}
                  color="neutral"
                  variant="outlined"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 20,
                    zIndex: 1202,
                    width: 20,
                    height: 20,
                    p: 0.25,
                    color: 'neutral.dark',
                    borderColor: isSelected ? activeColor : 'neutral.light',
                    '&:hover': {
                      borderColor: isSelected ? activeColor : 'neutral.main'
                    }
                  }}
                >
                  <ActionIcon
                    size={12}
                    color={mode === ThemeMode.DARK ? theme.palette.neutral[400] : theme.palette.neutral.main}
                    style={{ marginLeft: 1 }}
                  />
                </IconButton>
              );
            })}
        </Box>
      ) : (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          sx={{
            zIndex: 1201,
            '&:hover': {
              bgcolor: 'transparent'
            },
            ...(isParents && {
              color: textColor,
              p: 1,
              mr: 1
            }),
            '&.Mui-selected': {
              bgcolor: 'transparent',
              color: activeColor,
              '&:hover': {
                color: activeColor,
                bgcolor: 'transparent'
              }
            }
          }}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 38,
                color: iconColor,

                '& svg': {
                  color: iconColor
                },

                '& path': {
                  stroke: iconColor
                },

                ...(!drawerOpen &&
                  level === 1 && {
                    borderRadius: 1,
                    width: 46,
                    height: 46,
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: mode === ThemeMode.DARK ? 'neutral.light' : 'neutral.200'
                    }
                  }),

                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: mode === ThemeMode.DARK ? 'neutral.100' : 'rgba(16, 102, 26, 0.12)',
                    '&:hover': {
                      bgcolor: mode === ThemeMode.DARK ? 'neutral.200' : 'rgba(16, 102, 26, 0.12)'
                    }
                  })
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          <ListItemText
            primary={
              <Typography
                variant="h6"
                sx={{
                  color: isSelected ? activeColor : textColor,
                  fontWeight: isSelected ? 600 : 400
                }}
              >
                {item.title}
              </Typography>
            }
          />

          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              sx={{ ml: 1 }}
            />
          )}
        </ListItemButton>
      )}
    </>
  );
}
